import dayjs from "dayjs";
import { getItemWeight } from "../getItemWeight/getItemWeight.js";

export interface UpsWeightValidationResult {
  isValid: boolean;
  maxAllowedWeight: number;
  exceededItems: Array<{
    productName: string;
    weight: number;
    containerType: string;
  }>;
}

export interface ProductItem {
  Product: {
    name?: string | null;
    containerType?: string | null;
    unit?: string | null;
    displayUnit?: string | null;
    volume?: number | null;
    packQuantity?: number | null;
    weight?: number | null;
  };
}

/**
 * Validates if items can be shipped via UPS based on weight limits
 * @param items - Array of product items to validate
 * @param upsWeightChangeDate - The date when UPS weight changes take effect
 * @param comparisonDate - The date to compare against the weight change date (defaults to current date)
 * @returns Validation result with details about any exceeded items
 */
export const validateUpsWeight = (
  items: ProductItem[],
  upsWeightChangeDate: Date = new Date("2025-09-30T00:00:00Z"), // Default to production date
  comparisonDate: Date = new Date(), // Default to current date
): UpsWeightValidationResult => {
  // Determine weight limit based on feature flag and date
  const isAfterWeightChangeDate =
    dayjs(comparisonDate).isAfter(upsWeightChangeDate);

  // Use 25kg limit if feature flag is on AND we're after the change date
  // Otherwise use 59kg limit
  const maxAllowedWeight = isAfterWeightChangeDate ? 25 : 59;

  const exceededItems: UpsWeightValidationResult["exceededItems"] = [];

  for (const item of items) {
    const itemWeight = getItemWeight({
      containerType: item.Product.containerType ?? null,
      unit: (item.Product.displayUnit || item.Product.unit) ?? null,
      volume: item.Product.volume ?? null,
      packQuantity: item.Product.packQuantity ?? null,
      weight: item.Product.weight ?? null,
    });

    if (itemWeight > maxAllowedWeight) {
      exceededItems.push({
        productName: item.Product.name || "Unknown Product",
        weight: itemWeight,
        containerType: item.Product.containerType || "Unknown",
      });
    }
  }

  return {
    isValid: exceededItems.length === 0,
    maxAllowedWeight,
    exceededItems,
  };
};
